import { Fragment, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Combobox, Dialog, Transition } from "@headlessui/react";
import {
  Search,
  FileText,
  Calendar,
  Send,
  Inbox as InboxIcon,
  Settings as SettingsIcon,
  Mic2,
  Home,
  Sun,
  Moon,
  Sparkles,
  LogOut,
} from "lucide-react";
import { useTheme } from "../../lib/theme";
import { api } from "../../lib/api";
import { toast } from "../../lib/toast";

interface Command {
  id: string;
  label: string;
  icon: typeof Search;
  group: "Navegação" | "Ações" | "Aparência" | "Sessão";
  shortcut?: string;
  action: () => void | Promise<void>;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export const CommandPalette = ({ open, onClose }: CommandPaletteProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { resolved, toggle } = useTheme();
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const commands: Command[] = useMemo(
    () => [
      // Navegação
      { id: "nav-home", label: "Ir para Dashboard", icon: Home, group: "Navegação", action: () => navigate("/") },
      { id: "nav-drafts", label: "Ir para Drafts", icon: FileText, group: "Navegação", action: () => navigate("/drafts") },
      { id: "nav-scheduled", label: "Ir para Agendados", icon: Calendar, group: "Navegação", action: () => navigate("/scheduled") },
      { id: "nav-published", label: "Ir para Publicados", icon: Send, group: "Navegação", action: () => navigate("/published") },
      { id: "nav-inbox", label: "Ir para Inbox", icon: InboxIcon, group: "Navegação", action: () => navigate("/inbox") },
      { id: "nav-settings", label: "Ir para Settings", icon: SettingsIcon, group: "Navegação", action: () => navigate("/settings") },
      { id: "nav-voice", label: "Editar Voice profile", icon: Mic2, group: "Navegação", action: () => navigate("/settings/voice") },
      // Ações
      {
        id: "action-generate",
        label: "Gerar novo draft",
        icon: Sparkles,
        group: "Ações",
        action: async () => {
          try {
            await api.generation.now();
            toast.success("Geração iniciada", { description: "Draft em ~15-30s" });
            navigate("/drafts");
          } catch (err) {
            toast.error("Falha ao gerar", {
              description: err instanceof Error ? err.message : String(err),
            });
          }
        },
      },
      // Aparência
      {
        id: "appearance-theme",
        label: resolved === "dark" ? "Mudar para tema claro" : "Mudar para tema escuro",
        icon: resolved === "dark" ? Sun : Moon,
        group: "Aparência",
        action: toggle,
      },
      // Sessão
      {
        id: "session-logout",
        label: "Sair",
        icon: LogOut,
        group: "Sessão",
        action: async () => {
          try {
            await api.auth.logout();
          } catch {
            /* ignora */
          }
          queryClient.clear();
          toast.success("Sessão encerrada");
          navigate("/login", { replace: true });
        },
      },
    ],
    [navigate, queryClient, resolved, toggle]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter(
      (c) => c.label.toLowerCase().includes(q) || c.group.toLowerCase().includes(q)
    );
  }, [commands, query]);

  const grouped = useMemo(() => {
    const map = new Map<Command["group"], Command[]>();
    for (const c of filtered) {
      const arr = map.get(c.group) ?? [];
      arr.push(c);
      map.set(c.group, arr);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const onSelect = async (cmd: Command | null) => {
    if (!cmd) return;
    onClose();
    await cmd.action();
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-900/60 backdrop-blur-sm" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4 pt-20">
          <div className="flex min-h-full items-start justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-150"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl">
                <Combobox onChange={onSelect}>
                  <div className="flex items-center px-3 border-b border-zinc-200 dark:border-zinc-800">
                    <Search size={16} className="text-zinc-400 shrink-0" />
                    <Combobox.Input
                      autoFocus
                      placeholder="Buscar comando…"
                      onChange={(e) => setQuery(e.target.value)}
                      className="w-full px-3 py-3 bg-transparent text-sm outline-none placeholder:text-zinc-400 dark:placeholder:text-zinc-600"
                    />
                    <kbd className="hidden sm:inline-flex items-center font-mono text-[10px] text-zinc-500 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700 rounded px-1.5 h-5 shrink-0">
                      ESC
                    </kbd>
                  </div>

                  <Combobox.Options static className="max-h-80 overflow-y-auto py-2">
                    {grouped.length === 0 && (
                      <div className="px-4 py-8 text-center text-sm text-zinc-500 dark:text-zinc-400">
                        Nenhum comando encontrado.
                      </div>
                    )}
                    {grouped.map(([group, cmds]) => (
                      <div key={group}>
                        <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-500">
                          {group}
                        </div>
                        {cmds.map((cmd) => {
                          const Icon = cmd.icon;
                          return (
                            <Combobox.Option
                              key={cmd.id}
                              value={cmd}
                              className={({ active }) =>
                                `flex items-center gap-3 px-3 py-2 mx-1 rounded-md cursor-pointer text-sm transition-colors ${
                                  active
                                    ? "bg-accent/10 text-zinc-900 dark:text-zinc-100"
                                    : "text-zinc-700 dark:text-zinc-300"
                                }`
                              }
                            >
                              <Icon size={15} className="text-zinc-500 dark:text-zinc-400 shrink-0" />
                              <span className="flex-1">{cmd.label}</span>
                            </Combobox.Option>
                          );
                        })}
                      </div>
                    ))}
                  </Combobox.Options>
                </Combobox>

                <div className="px-3 py-2 border-t border-zinc-200 dark:border-zinc-800 flex items-center gap-3 text-[11px] text-zinc-500 dark:text-zinc-500">
                  <span>
                    <kbd className="font-mono border border-zinc-200 dark:border-zinc-700 rounded px-1">↑↓</kbd> navegar
                  </span>
                  <span>
                    <kbd className="font-mono border border-zinc-200 dark:border-zinc-700 rounded px-1">↵</kbd> selecionar
                  </span>
                  <span className="ml-auto">
                    <kbd className="font-mono border border-zinc-200 dark:border-zinc-700 rounded px-1">⌘K</kbd> abrir
                  </span>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
